import { HTMLMotionProps } from 'motion/react';
import { ReactNode, useEffect, useState } from 'react';

export type AnimationProps = HTMLMotionProps<'div'>;

export type NavigateOptionProps = { animation?: AnimationProps };

const forwardAnim: AnimationProps = {
  initial: {
    translateX: 500,
    opacity: 0,
  },
  animate: {
    translateX: 0,
    opacity: 1,
  },
  exit: {
    translateX: 500,
    opacity: 1,
  },
  transition: { type: 'spring', bounce: 0.25 },
};

const backwardAnim: AnimationProps = {
  initial: {
    translateX: -500,
    opacity: 0,
  },
  animate: {
    translateX: 0,
    opacity: 1,
  },
  exit: {
    translateX: -500,
    opacity: 1,
  },
  transition: { type: 'spring', bounce: 0.25 },
};

export type NavigateProps = (
  newScreen: ReactNode,
  navOptions?: NavigateOptionProps,
) => void;

export type GoBackProps = () => void;

export type StackProps = {
  navigate: NavigateProps;
  goBack: GoBackProps;
  current: ReactNode;
  canGoBack: boolean;
  animation: AnimationProps | undefined;
  setAnimation: (newAnimation: AnimationProps) => void;
  stack: ReactNode[];
};

/**
 * Keeps track of arbitrary "stack" of screens.
 */
export default function useStack(
  initial: ReactNode,
  dependancyList: unknown[] = [],
): StackProps {
  const [stack, setStack] = useState<ReactNode[]>([initial]);
  const [anim, setAnim] = useState<AnimationProps | undefined>();

  useEffect(() => {
    setStack([initial]);
    setAnim(undefined);
  }, [...dependancyList]);

  const canGoBack = stack.length > 1;

  const current = stack[stack.length - 1];

  /**
   * Move in the stack.
   */
  const navigate: NavigateProps = (newScreen, navOptions) => {
    const animation = navOptions?.animation;

    if (newScreen) {
      const newStack = [...stack];
      newStack.push(newScreen);
      setAnim(animation);
      setStack(newStack);
    }
  };

  /**
   * Go backward in the stack.
   */
  const goBack: GoBackProps = () => {
    const newStack = [...stack];
    if (canGoBack) {
      newStack.pop();
      setAnim(backwardAnim);
      setStack(newStack);
    }
  };

  return {
    navigate,
    goBack,
    stack,
    canGoBack,
    current,
    animation: anim,
    setAnimation: setAnim,
  };
}

export const ANIMATIONS = {
  forward: forwardAnim,
  backward: backwardAnim,
};
